// Widget search for the French pages. Same scoring idea as
// helpers/widgetSearch.ts (name, then category, then description), with
// four changes for French visitors:
// - accents, cedillas and ligatures are folded, so "réservation",
//   "reservation" and "Réservation" all match (the English tokenizer splits
//   words on any non a-z letter),
// - French words are mapped to the English words in widget names, so
//   "formulaire de contact" finds Contact Form, whose name stays English,
// - French plurals (-s, -x, -aux) are reduced to a known singular, see
//   singular(); multi-word terms ("compte à rebours", "carte à gratter")
//   are covered by their distinctive word ("rebours", "gratter"),
// - a typed word in a name outweighs a mapped one (see expand()).
// Searches both the shown name and the English name.
// Client-safe: imports no data, so the French copy is not bundled.

export interface IEsSearchable {
  name: string;
  enName: string;
  teaser: string;
  categories: string[];
  order: number;
}

// French query word -> English words in widget names. Matched on whole,
// folded words (no accents, "œ" -> "oe"). Plurals are covered by
// singular() below.
const FR_TO_EN: Record<string, string[]> = {
  // Reviews and trust
  avis: ["review", "reviews", "testimonials"],
  note: ["rating", "review"],
  etoile: ["review", "rating"],
  temoignage: ["testimonials"],
  recommandation: ["testimonials", "review"],
  client: ["testimonials", "review"],
  confiance: ["trust", "badges"],
  badge: ["badge", "badges", "trust"],
  // Forms and bookings
  formulaire: ["form"],
  demande: ["contact", "form"],
  inscription: ["registration", "form", "subscription"],
  reservation: ["booking", "appointment"],
  rendez: ["appointment", "booking"],
  calendrier: ["calendar"],
  evenement: ["event", "calendar"],
  consentement: ["consent", "cookie"],
  commande: ["order"],
  candidature: ["job", "application"],
  // Timers and counters
  rebours: ["countdown"],
  minuteur: ["timer", "countdown", "pomodoro"],
  chrono: ["timer"],
  chronometre: ["timer"],
  compteur: ["counter", "countup"],
  visiteur: ["visitor"],
  chiffre: ["numbers", "counter"],
  nombre: ["numbers", "counter"],
  statistique: ["numbers", "counter", "charts"],
  horloge: ["clock"],
  heure: ["clock", "hours"],
  fuseau: ["clock"],
  horaire: ["hours"],
  ouverture: ["hours"],
  meteo: ["weather"],
  // Images, video and audio
  carrousel: ["carousel"],
  diaporama: ["slideshow", "slider", "carousel"],
  galerie: ["gallery"],
  photo: ["image", "gallery"],
  mur: ["wall"],
  grille: ["grid"],
  loupe: ["magnifier"],
  musique: ["music", "audio"],
  lecteur: ["player"],
  maquette: ["mockup"],
  animation: ["animated", "lottie"],
  anime: ["animated"],
  visite: ["tour"],
  virtuel: ["virtual", "tour"],
  virtuelle: ["virtual", "tour"],
  panorama: ["virtual", "tour"],
  feuilletable: ["flipbook"],
  livre: ["flipbook"],
  // Popups, bars and buttons
  bouton: ["button"],
  barre: ["bar"],
  bandeau: ["bar", "banner"],
  banniere: ["banner"],
  fenetre: ["popup"],
  progression: ["progress"],
  progres: ["progress"],
  alerte: ["notification"],
  annonce: ["announcements", "notification"],
  actualite: ["news", "announcements"],
  nouveaute: ["news", "announcements"],
  defilant: ["ticker", "slider", "scroll"],
  defilement: ["scroll"],
  haut: ["top"],
  coupon: ["coupon"],
  reduction: ["coupon"],
  remise: ["coupon"],
  promo: ["coupon"],
  telephone: ["call"],
  appel: ["call"],
  // Sales and payments
  prix: ["pricing"],
  tarif: ["pricing"],
  tableau: ["table", "tables"],
  comparatif: ["comparison"],
  comparaison: ["comparison", "before"],
  paiement: ["payment", "paypal", "stripe"],
  don: ["donation", "donate"],
  abonnement: ["subscription"],
  produit: ["product", "catalog"],
  catalogue: ["catalog"],
  boutique: ["catalog", "product"],
  devise: ["currency"],
  convertisseur: ["converter"],
  taille: ["size"],
  immobilier: ["real", "estate"],
  // Content and layout
  question: ["faq"],
  accordeon: ["accordion"],
  onglet: ["tabs"],
  carte: ["card", "map"],
  localisation: ["map", "maps"],
  adresse: ["map", "maps"],
  entreprise: ["business"],
  annuaire: ["listings", "business"],
  liste: ["list", "listings"],
  chronologie: ["timeline"],
  frise: ["timeline"],
  citation: ["quotes"],
  titre: ["headline"],
  graphique: ["charts"],
  diagramme: ["diagrams", "charts"],
  equipe: ["team"],
  membre: ["team", "members"],
  emploi: ["job"],
  recrutement: ["job"],
  cours: ["course"],
  formation: ["course"],
  glossaire: ["glossary"],
  lien: ["link", "links"],
  pied: ["footer"],
  // Games and engagement
  sondage: ["poll", "survey"],
  vote: ["votes", "voting", "poll"],
  jeu: ["game", "quiz"],
  personnalite: ["personality"],
  concours: ["contest", "brackets"],
  tournoi: ["brackets", "tournament"],
  classement: ["leaderboard", "tier", "ranking"],
  roue: ["wheel"],
  fortune: ["wheel"],
  gratter: ["scratch"],
  avant: ["before"],
  apres: ["after", "before"],
  retournable: ["flip"],
  retourner: ["flip"],
  commentaire: ["comments"],
  // Social and chat
  social: ["social"],
  reseau: ["social"],
  partage: ["share"],
  partager: ["share"],
  flux: ["feed", "feeds"],
  publication: ["feed", "posts"],
  conversation: ["chat", "conversation"],
  discussion: ["chat"],
  messagerie: ["chat"],
  // Tools
  verification: ["verification", "age"],
  traduction: ["translator"],
  traducteur: ["translator"],
  recherche: ["search"],
  accessibilite: ["accessibility"],
  calculatrice: ["calculator"],
  calculateur: ["calculator"],
  bloqueur: ["adblocker"],
  clic: ["click"],
  droit: ["right"],
};

// Lowercase, drop accents and cedillas ("réservation" -> "reservation"),
// "œ" -> "oe", "æ" -> "ae".
const fold = (text: string) =>
  text
    .toLowerCase()
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// French names carry "de", "et", "pour" and the like ("Avis et confiance"),
// so these would match nearly every widget. Elided "l'", "d'" are dropped
// by the length filter in tokenize().
const STOPWORDS = new Set([
  "le", "la", "les", "un", "une", "des", "de", "du", "et", "ou", "pour",
  "avec", "sur", "dans", "en", "au", "aux", "par", "mon", "ma", "mes",
  "votre", "vos", "ton", "ta", "tes", "son", "sa", "ses", "ce", "cette",
  "ces", "qui", "que", "vous", "nous", "mettre", "ajouter",
  "widget", "widgets", "site", "sites", "web", "internet",
]);

const tokenize = (text: string) =>
  fold(text)
    .split(/[^a-z0-9&]+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));

// Folded French plurals: "formulaires" -> "formulaire", "jeux" -> "jeu",
// "tableaux" -> "tableau", "sociaux" -> "social". Only strips when the
// result is a known word, except a final "s" (English plurals like
// "reviews", and French ones the map does not list).
const singular = (w: string) => {
  if (FR_TO_EN[w]) return w;
  if (w.length > 4 && w.endsWith("aux") && FR_TO_EN[`${w.slice(0, -3)}al`])
    return `${w.slice(0, -3)}al`;
  for (const end of ["s", "x"]) {
    if (w.length > end.length + 2 && w.endsWith(end) && FR_TO_EN[w.slice(0, -end.length)])
      return w.slice(0, -end.length);
  }
  if (w.length > 3 && w.endsWith("s")) return w.slice(0, -1);
  return w;
};

// Words the visitor typed, and the English words they map to. A typed word
// found in a name counts more than a mapped one, so "bouton whatsapp" ranks
// the WhatsApp widget above every other button.
function expand(query: string) {
  const words = tokenize(query);
  const typed = Array.from(new Set([...words, ...words.map(singular)]));
  const mapped = words
    .flatMap((w) => FR_TO_EN[w] || FR_TO_EN[singular(w)] || [])
    .filter((w) => !typed.includes(w));
  return { typed, mapped: Array.from(new Set(mapped)) };
}

export function searchWidgetsFr<T extends IEsSearchable>(
  widgets: T[],
  query: string
): T[] {
  const { typed, mapped } = expand(query);
  if (!typed.length) return widgets;
  return widgets
    .map((w) => {
      const name = fold(`${w.name} ${w.enName}`);
      const cats = fold(w.categories.join(" "));
      const teaser = fold(w.teaser);
      let score = 0;
      typed.forEach((t) => {
        if (name.includes(t)) score += 6;
        if (cats.includes(t)) score += 2;
        if (teaser.includes(t)) score += 1;
      });
      mapped.forEach((t) => {
        if (name.includes(t)) score += 4;
        if (cats.includes(t)) score += 2;
        if (teaser.includes(t)) score += 1;
      });
      return { w, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.w.order - b.w.order)
    .map(({ w }) => w);
}
